import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { AdornedInput, Box, DropdownSelect, FormField, FormHelp, Label } from '@zillow/constellation-10';

import { ModularListingContext } from '../../../../../containers/CreateListingPage/context';

import { PropertiesSelectors, RoutingSelectors, UserSelectors } from '../../../../../selectors';
import { useSelector } from '../../../../../selectors/useSelector';

import { useForm } from '../../../../../hooks';
import { useSetListingCriteria } from '../../../../../hooks/queries/listingCriteria';

import PropertiesActions from '../../../../../actions/PropertiesActions';
import actions from '../../../../../containers/CreateListingPage/actions';

import { postingConstraints } from '../../../../../utils/formValidation';
import typeCheckUtils from '../../../../../utils/typeCheckUtils';

import Criterion from '../../../../../model/ListingCriteria/Criterion';

import { DISPLAY_SERVER_ERROR_AT } from '../../../../../constants/WizardConstants';
import {
  LC_PET_POLICY_FORM_CONSTANTS,
  NEGOTIABLE,
  PET_POLICY,
  THREE_PLUS,
  TYPES_MAP,
} from '../../../../../constants/ListingCriteriaConstants';

import ListingCriteria from '../../../../../model/ListingCriteria';
import PetPolicyAndPetFeeCSEvents from '../../../../../model/ClickstreamEvent/PetPolicyAndPetFeeCSEvents';
import WizardServerError from '../../../../../model/WizardServerError';

import LCNegotiableFieldSet from '../../../../../components/WizardModularListing/LCNegotiableFieldSet';

import type { ChangeEvent } from 'react';
import type { ErrorRMWProps } from '../../../../../model/ErrorRMW';
import type { CriterionProps } from '../../../../../model/ListingCriteria/Criterion';

const { CAT_COUNT_OPTIONS, DOG_COUNT_OPTIONS, DOG_SIZE_OPTIONS, LABEL_TO_VALUE_MAP, VALUE_TO_LABEL_MAP } =
  LC_PET_POLICY_FORM_CONSTANTS;

const requiredFieldErrorText = 'Required field';

const inputField = PET_POLICY;

export const threeOrMoreMapper = (arg: string) => {
  if (!arg) {
    return arg;
  }

  const value = typeCheckUtils.Number(arg);

  if (arg === THREE_PLUS || (value !== null && value < 3)) {
    return arg;
  } else if (value !== null && value >= 3) {
    return THREE_PLUS;
  }

  return arg;
};

type MockEventProps = {
  target: {
    name: string;
    value: string | boolean;
  };
  persist?: () => void;
};

type SaveEvent = ChangeEvent<HTMLInputElement> | MockEventProps;

const LCPetPolicyFormFieldContainer = () => {
  const dispatch = useDispatch();

  const {
    modularListingState: { isSaveComplete, serverErrorObj, hasFieldError: hasFormFieldError },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const listingAlias = useSelector((state) => RoutingSelectors.makeListingId(state));

  const currentProperty = useSelector((state) => PropertiesSelectors.makeCurrentProperty(state));

  const user = useSelector((state) => UserSelectors.makeUser(state));

  const {
    listingCriteria: { isEnabled, criteria },
    isFeed,
  } = currentProperty;

  const lisingCriteriaPetPolicy = useMemo(
    () =>
      (criteria as Array<CriterionProps>)
        .filter((criterion) => criterion.type.includes(inputField))
        .reduce(
          (prevVal: Record<string, unknown>, criterion: CriterionProps) => {
            const { type, criterion: value, negotiable } = criterion;

            prevVal[type] = value;
            prevVal.negotiable = prevVal.negotiable ?? negotiable;

            return prevVal;
          },
          { negotiable: null } as Record<string, unknown>,
        ),
    [],
  );

  const {
    inputs,
    errorObj,
    hasFieldError,
    handleChange: formHandleChange,
  } = useForm({ ...lisingCriteriaPetPolicy }, postingConstraints);

  const { mutateAsync: updateListingCriteria } = useSetListingCriteria();

  const onFocusRef = useRef<{
    value: string | boolean;
    isInputFocused: boolean;
    touched: boolean;
  }>({
    value: '',
    isInputFocused: false,
    touched: false,
  });

  const [isCatCountEmpty, setIsCatCountEmpty] = useState(false);
  const [isDogCountEmpty, setIsDogCountEmpty] = useState(false);

  const isDogSizeDisabled = isFeed || inputs[TYPES_MAP.PET_POLICY_DOG_COUNT] === '0';
  const petPolicyDogSizeValue =
    // @ts-expect-error
    VALUE_TO_LABEL_MAP[TYPES_MAP.PET_POLICY_DOG_SIZE + threeOrMoreMapper(inputs[TYPES_MAP.PET_POLICY_DOG_SIZE])];

  const isDepositAndRentDisabled =
    inputs[TYPES_MAP.PET_POLICY_CAT_COUNT] === '0' && inputs[TYPES_MAP.PET_POLICY_DOG_COUNT] === '0';

  useEffect(() => {
    actions.updateIsSaveDisabled({ reducerDispatch, isSaveDisabled: true });

    return () => {
      enableSaveButton();
    };
  }, []);

  useEffect(() => {
    if (isSaveComplete) {
      save(
        {
          target: {
            name: NEGOTIABLE,
            // @ts-expect-error
            value: inputs.negotiable,
          },
        },
        true,
      );
    }
  }, [isSaveComplete]);

  /**
   * callback to handle relational actions between form fields
   */
  useEffect(() => {
    if (isCatCountEmpty) {
      setIsCatCountEmpty(!inputs[TYPES_MAP.PET_POLICY_CAT_COUNT]);
    }
    if (isDogCountEmpty) {
      setIsDogCountEmpty(!inputs[TYPES_MAP.PET_POLICY_DOG_COUNT]);
    }

    /**
     * if both `deposit` and `rent` are disabled and `inputs.deposit` has value, clear `inputs.deposit`
     */
    if (inputs[TYPES_MAP.PET_POLICY_PET_DEPOSIT] && isDepositAndRentDisabled) {
      handleFormChange({
        target: {
          name: TYPES_MAP.PET_POLICY_PET_DEPOSIT,
          value: '',
        },
      });

      return;
    }

    /**
     * if both `deposit` and `rent` are disabled and `inputs.rent` has value, clear `inputs.rent`
     */
    if (inputs[TYPES_MAP.PET_POLICY_PET_RENT] && isDepositAndRentDisabled) {
      handleFormChange({
        target: {
          name: TYPES_MAP.PET_POLICY_PET_RENT,
          value: '',
        },
      });

      return;
    }

    /**
     * if the dog size is disabled and the dog size value is empty, set the dog size to large
     */
    if (isDogSizeDisabled === false && petPolicyDogSizeValue === '') {
      // update form value instead of direct response from map to keep BE and FE in sync
      handleFormChange({
        target: {
          name: TYPES_MAP.PET_POLICY_DOG_SIZE,
          value: 'large',
        },
      });

      return;
    }
  }, [inputs]);

  const enableSaveButton = useCallback(() => {
    actions.updateIsSaveDisabled({ reducerDispatch, isSaveDisabled: false });
  }, []);

  const handleFormChange = useCallback(
    (data: SaveEvent) => {
      // @ts-expect-error
      formHandleChange(data);

      if (!onFocusRef.current.touched) {
        onFocusRef.current.touched = true;

        enableSaveButton();
      }
    },
    [formHandleChange],
  );

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const updateServerError = (message = '', fieldName = '') => {
    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt: DISPLAY_SERVER_ERROR_AT.FIELD,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const save = (event: SaveEvent, isSavingAllFields = false) => {
    let hasFieldErrorOnRequiredFields = false;
    if (isSavingAllFields) {
      setIsCatCountEmpty(!inputs[TYPES_MAP.PET_POLICY_CAT_COUNT]);
      setIsDogCountEmpty(!inputs[TYPES_MAP.PET_POLICY_DOG_COUNT]);

      hasFieldErrorOnRequiredFields =
        !inputs[TYPES_MAP.PET_POLICY_CAT_COUNT] || !inputs[TYPES_MAP.PET_POLICY_DOG_COUNT];
    }

    const { value, name: fieldName } = event.target;

    if (onFocusRef.current.isInputFocused) {
      onFocusRef.current.isInputFocused = false;

      if (onFocusRef.current.value === value) {
        onFocusRef.current.value = '';

        return;
      }
    }

    const isNotPetDepositAndPetRent =
      fieldName !== TYPES_MAP.PET_POLICY_PET_DEPOSIT && fieldName !== TYPES_MAP.PET_POLICY_PET_RENT;

    // field error check needs to be above must click on cta check
    if (hasFieldErrorOnRequiredFields || ((isNotPetDepositAndPetRent || value) && hasFieldError(fieldName, value))) {
      actions.updateHasFieldError({ reducerDispatch, hasFieldError: true });
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

      return;
    }

    if (hasFormFieldError && isSavingAllFields) {
      const newHasFormFieldError = Object.values(errorObj).some((errorValue) => errorValue);

      actions.updateHasFieldError({
        reducerDispatch,
        hasFieldError: newHasFormFieldError,
      });

      // return if there are other errors
      if (newHasFormFieldError) {
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

        return;
      }
    }

    // return if user must click on cta
    if (!isSaveComplete) {
      return;
    }

    /**
     * data here are safe to `Criterion` cast without retrieving old data
     * because `alert, min, and max` are `empty` for all the `LCPetPolicy` fields
     */
    let dataToSave: Array<CriterionProps>;
    if (isSavingAllFields) {
      dataToSave = [];

      for (const key in inputs) {
        // TODO: remove once we deprecate PET_POLICY_DOG_COUNT
        const newPetsTypeConditions =
          key !== TYPES_MAP.PET_POLICY_LARGE_DOG_COUNT && key !== TYPES_MAP.PET_POLICY_SMALL_DOG_COUNT;

        if (key !== NEGOTIABLE && newPetsTypeConditions) {
          dataToSave.push(
            Criterion({
              type: key,
              // @ts-expect-error
              criterion: inputs[key],
              // @ts-expect-error
              negotiable: inputs.negotiable,
            }),
          );
        }
      }
    } else {
      dataToSave =
        fieldName === NEGOTIABLE
          ? (criteria as Array<CriterionProps>)
              .filter((criterion) => criterion.type in inputs)
              .map((criterion: CriterionProps) => Criterion({ ...criterion, negotiable: value as boolean }))
          : [
              Criterion({
                type: fieldName,
                criterion: value as string,
                // @ts-expect-error
                negotiable: inputs.negotiable === null ? false : inputs.negotiable,
              }),
            ];
    }

    if (
      (fieldName === TYPES_MAP.PET_POLICY_CAT_COUNT &&
        !Number((value as string)[0]) &&
        // @ts-expect-error
        !Number(inputs[TYPES_MAP.PET_POLICY_DOG_COUNT][0])) ||
      (fieldName === TYPES_MAP.PET_POLICY_DOG_COUNT &&
        !Number((value as string)[0]) &&
        // @ts-expect-error
        !Number(inputs[TYPES_MAP.PET_POLICY_CAT_COUNT][0]))
    ) {
      const dataToAppend = [
        {
          type: TYPES_MAP.PET_POLICY_PET_DEPOSIT,
          criterion: '',
          negotiable: inputs.negotiable === null ? false : inputs.negotiable,
        },
        {
          type: TYPES_MAP.PET_POLICY_PET_RENT,
          criterion: '',
          negotiable: inputs.negotiable === null ? false : inputs.negotiable,
        },
      ];

      // @ts-expect-error
      dataToAppend.forEach((criterion) => dataToSave.push(Criterion(criterion)));
    }

    updateListingCriteria({ listingAlias, criteria: dataToSave })
      .then(() => {
        dispatch(
          PropertiesActions.updateCurrentProperty({
            listingCriteria: ListingCriteria({
              isEnabled,
              criteria: [...criteria, ...dataToSave],
            }),
          }),
        );

        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((err: { error: ErrorRMWProps }) => {
        const {
          error: { message },
        } = err;

        updateServerError(message, fieldName);
      });
  };

  const handleChange = (event: SaveEvent) => {
    event?.persist?.();

    clearServerError();
    handleFormChange(event);
  };

  const handleDropDownSelectChange = (
    groupName:
      | typeof TYPES_MAP.PET_POLICY_CAT_COUNT
      | typeof TYPES_MAP.PET_POLICY_DOG_COUNT
      | typeof TYPES_MAP.PET_POLICY_DOG_SIZE,
    value: string,
  ) => {
    const mockEvent = {
      target: {
        name: groupName,
        value: LABEL_TO_VALUE_MAP[value as keyof typeof LABEL_TO_VALUE_MAP],
      },
      persist: () => {},
    };

    handleChange(mockEvent);
    save(mockEvent);

    if (groupName === TYPES_MAP.PET_POLICY_DOG_COUNT) {
      PetPolicyAndPetFeeCSEvents.NumberOfDogsAllowed({ currentProperty, user });
    }

    if (groupName === TYPES_MAP.PET_POLICY_DOG_SIZE) {
      PetPolicyAndPetFeeCSEvents.DogSizeAllowed({ currentProperty, user });
    }
  };

  const handleNegotiableChange = (event: SaveEvent) => {
    const {
      target: { name, value },
      persist,
    } = event;
    const mockEvent = {
      target: {
        name,
        value: value === 'true',
      },
      persist,
    };

    handleChange(mockEvent);
  };

  const handleFocus = (event: SaveEvent) => {
    const {
      target: { value },
    } = event;

    onFocusRef.current.value = value;
    onFocusRef.current.isInputFocused = true;
  };

  return (
    <div data-testid="LCPetPolicyFormFieldContainer">
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          gridTemplateRows: {
            base: 'repeat(5, 1fr)',
            lg: 'repeat(3, 1fr)',
          },
          gap: '400',
        }}
      >
        <Box css={{ display: 'grid', gridColumnEnd: { base: 'span 12', lg: 'span 4' }, gridRow: '1' }}>
          <FormField
            data-testid="LCPetPolicyFormFieldCats"
            disabled={isFeed}
            error={isCatCountEmpty}
            label={<Label required={!isFeed}>Cats</Label>}
            control={
              <DropdownSelect
                value={
                  VALUE_TO_LABEL_MAP[
                    // @ts-expect-error
                    TYPES_MAP.PET_POLICY_CAT_COUNT + threeOrMoreMapper(inputs[TYPES_MAP.PET_POLICY_CAT_COUNT])
                  ]
                }
                placeholder="How many?"
                options={CAT_COUNT_OPTIONS}
                onChange={(value) => handleDropDownSelectChange(TYPES_MAP.PET_POLICY_CAT_COUNT, value)}
              />
            }
            description={<FormHelp error={isCatCountEmpty}>{isCatCountEmpty && requiredFieldErrorText}</FormHelp>}
          />
        </Box>
        <Box
          css={{
            display: 'grid',
            gridColumnEnd: { base: 'span 12', lg: 'span 4' },
            gridRow: '2',
          }}
        >
          <FormField
            data-testid="LCPetPolicyFormFieldDogs"
            disabled={isFeed}
            error={isDogCountEmpty}
            label={<Label required={!isFeed}>Dogs</Label>}
            control={
              <DropdownSelect
                value={
                  VALUE_TO_LABEL_MAP[
                    // @ts-expect-error
                    TYPES_MAP.PET_POLICY_DOG_COUNT + threeOrMoreMapper(inputs[TYPES_MAP.PET_POLICY_DOG_COUNT])
                  ]
                }
                placeholder="How many?"
                options={DOG_COUNT_OPTIONS}
                onChange={(value) => handleDropDownSelectChange(TYPES_MAP.PET_POLICY_DOG_COUNT, value)}
                data-eventid={PetPolicyAndPetFeeCSEvents.NumberOfDogsAllowed.data_event_id}
              />
            }
            description={<FormHelp error={isDogCountEmpty}>{isDogCountEmpty && requiredFieldErrorText}</FormHelp>}
          />
        </Box>
        <Box
          css={{
            display: 'grid',
            gridColumnEnd: { base: 'span 12', lg: 'span 8' },
            gridRow: { base: '3', lg: '2' },
          }}
        >
          <FormField
            data-testid="LCPetPolicyFormFieldDogSize"
            label={<Label>Maximum dog size</Label>}
            disabled={isDogSizeDisabled}
            control={
              <DropdownSelect
                value={petPolicyDogSizeValue}
                placeholder="Select size"
                options={DOG_SIZE_OPTIONS}
                onChange={(value) => handleDropDownSelectChange(TYPES_MAP.PET_POLICY_DOG_SIZE, value)}
                data-eventid={PetPolicyAndPetFeeCSEvents.DogSizeAllowed.data_event_id}
              />
            }
          />
        </Box>
        <Box
          css={{
            display: 'grid',
            gridColumnEnd: { base: 'span 12', lg: 'span 6' },
            gridRow: { base: '4', lg: '3' },
          }}
        >
          <FormField
            data-testid="LCPetPolicyFormFieldDeposit"
            disabled={isFeed || isDepositAndRentDisabled}
            // @ts-expect-error
            error={!!errorObj[TYPES_MAP.PET_POLICY_PET_DEPOSIT]}
            label={<Label>Pet deposit</Label>}
            control={
              <AdornedInput
                startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                input={
                  <AdornedInput.Input
                    name={TYPES_MAP.PET_POLICY_PET_DEPOSIT}
                    value={String(inputs[TYPES_MAP.PET_POLICY_PET_DEPOSIT] || '')}
                    onFocus={handleFocus}
                    onChange={handleChange}
                    onBlur={save}
                    // @ts-expect-error
                    error={!!errorObj[TYPES_MAP.PET_POLICY_PET_DEPOSIT]}
                  />
                }
              />
            }
            description={
              // @ts-expect-error
              <FormHelp error={!!errorObj[TYPES_MAP.PET_POLICY_PET_DEPOSIT]}>
                {/* @ts-expect-error */}
                {errorObj[TYPES_MAP.PET_POLICY_PET_DEPOSIT]
                  ? // @ts-expect-error
                    errorObj[TYPES_MAP.PET_POLICY_PET_DEPOSIT]
                  : 'Set to $0 if not required'}
              </FormHelp>
            }
          />
        </Box>
        <Box
          css={{
            display: 'grid',
            gridColumnEnd: { base: 'span 12', lg: 'span 6' },
            gridRow: { base: '5', lg: '3' },
          }}
        >
          <FormField
            data-testid="LCPetPolicyFormFieldRent"
            disabled={isFeed || isDepositAndRentDisabled}
            // @ts-expect-error
            error={!!errorObj[TYPES_MAP.PET_POLICY_PET_RENT]}
            label={<Label>Pet rent</Label>}
            control={
              <AdornedInput
                startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                input={
                  <AdornedInput.Input
                    name={TYPES_MAP.PET_POLICY_PET_RENT}
                    value={String(inputs[TYPES_MAP.PET_POLICY_PET_RENT] || '')}
                    onFocus={handleFocus}
                    onChange={handleChange}
                    onBlur={save}
                    // @ts-expect-error
                    error={!!errorObj[TYPES_MAP.PET_POLICY_PET_RENT]}
                  />
                }
                endAdornment={<AdornedInput.Adornment>/mo</AdornedInput.Adornment>}
              />
            }
            description={
              // @ts-expect-error
              <FormHelp error={!!errorObj[TYPES_MAP.PET_POLICY_PET_RENT]}>
                {/* @ts-expect-error */}
                {errorObj[TYPES_MAP.PET_POLICY_PET_RENT]
                  ? // @ts-expect-error
                    errorObj[TYPES_MAP.PET_POLICY_PET_RENT]
                  : 'Set to $0 if not required'}
              </FormHelp>
            }
          />
        </Box>
      </Box>

      <LCNegotiableFieldSet
        isDisabled={isFeed}
        handleChange={handleNegotiableChange}
        // @ts-expect-error
        checkedValue={inputs.negotiable}
      />
    </div>
  );
};

export default LCPetPolicyFormFieldContainer;
